import { motion } from "framer-motion";
import { Download, Share2, CreditCard, Shield, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { useGetMemberDashboardSummary, getGetMemberDashboardSummaryQueryKey, API_BASE } from "@/lib/api-client-react";
import { useToast } from "@/hooks/use-toast";

function QRCodeSVG() {
  const size = 80;
  const cells = Array.from({ length: 7 }, (_, row) =>
    Array.from({ length: 7 }, (_, col) => {
      if ((row < 2 && col < 2) || (row < 2 && col > 4) || (row > 4 && col < 2)) return true;
      return Math.random() > 0.5;
    })
  );
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded-lg bg-white p-1">
      {cells.map((row, r) =>
        row.map((cell, c) =>
          cell ? <rect key={`${r}-${c}`} x={c * 11 + 2} y={r * 11 + 2} width={9} height={9} rx={1} fill="#111827" /> : null
        )
      )}
    </svg>
  );
}

function BarcodeStripes() {
  const bars = Array.from({ length: 60 }, (_, i) => ({ width: Math.random() > 0.5 ? 2 : 1, gap: Math.random() > 0.7 ? 3 : 2 }));
  let x = 0;
  return (
    <svg width="100%" height="40" viewBox="0 0 200 40">
      {bars.map((bar, i) => {
        const rect = <rect key={i} x={x} y={0} width={bar.width} height={40} fill="white" opacity={0.85} />;
        x += bar.width + bar.gap;
        return x < 200 ? rect : null;
      })}
    </svg>
  );
}

export default function MembershipCard() {
  const [user, setUser] = useState<any>(null);
  const [memberId, setMemberId] = useState<string>("DC-PENDING");
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const json = await res.json();
        if (json.success) {
          setUser(json.data);
          if (json.data.member?.id) {
            setMemberId(`DC-${json.data.member.id.substring(0, 8).toUpperCase()}`);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchUserData();
  }, []);

  const { data } = useGetMemberDashboardSummary({ query: { queryKey: getGetMemberDashboardSummaryQueryKey() } });
  const ins = data?.insuranceSummary;

  const memberName = user ? (user.displayName || `${user.firstName} ${user.lastName}`).toUpperCase() : "MEMBER NAME";
  const fallbackMemberId = memberId;
  const activeMemberId = ins?.memberId ?? fallbackMemberId;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        logging: false
      });
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `StraightDoc_ID_Card_${memberName.replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        title: "ID Card Downloaded",
        description: "Your digital membership card has been saved as an image."
      });
    } catch (err: any) {
      toast({
        title: "Download Failed",
        description: err.message || "Failed to export membership card image.",
        variant: "destructive"
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "StraightDoc Member Access Card",
          text: `My StraightDoc Member Access Card (${memberName} - ID: ${activeMemberId})`
        });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(`StraightDoc Member Card - Name: ${memberName}, ID: ${activeMemberId}`);
      toast({
        title: "Copied to Clipboard",
        description: "Member Card details copied to clipboard."
      });
    }
  };

  return (
    <MemberLayout>
      <div className="w-full max-w-2xl mx-auto space-y-6 sm:space-y-7 pt-3 sm:pt-5 pb-16">
        <div className="flex items-center gap-3.5">
          <div className="p-2.5 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0 text-primary border border-primary/20 shadow-xs">
            <CreditCard className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">My StraightDoc Card</h1>
            <p className="text-xs sm:text-sm font-medium text-slate-500 dark:text-slate-400">Your personal access to StraightDoc services and providers</p>
          </div>
        </div>

        {/* Front Card */}
        <motion.div
          initial={{ opacity: 0, y: 20, rotateX: -10 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.5 }}
          style={{ perspective: 1000 }}
          className="mb-1"
        >
          <div
            ref={cardRef}
            className="relative rounded-3xl overflow-hidden shadow-[0_20px_50px_-10px_rgba(2,132,199,0.35),0_0_0_1px_rgba(255,255,255,0.1)]"
            style={{ background: "linear-gradient(135deg, #090e17 0%, #0d1b2a 28%, #1b263b 55%, #0f1f38 80%, #0284c7 100%)" }}
          >
            {/* Subtle textured dot mesh */}
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)", backgroundSize: "14px 14px" }} />

            {/* Premium glassmorphic effects & ambient glows */}
            <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-cyan-400/20 blur-3xl pointer-events-none" />
            <div className="absolute -left-12 -bottom-12 w-56 h-56 rounded-full bg-blue-600/25 blur-3xl pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-white/5 opacity-80 pointer-events-none" />
            <div className="absolute inset-0 border border-white/20 rounded-3xl pointer-events-none" />

            <div className="relative p-6 sm:p-8 sm:py-9 z-10">
              {/* Header */}
              <div className="flex items-start justify-between mb-8 sm:mb-9">
                <div>
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/25 shadow-md">
                      <Shield className="w-5 h-5 text-cyan-300 drop-shadow" />
                    </div>
                    <span className="text-white font-black text-xl sm:text-2xl tracking-tight drop-shadow-md">StraightDoc</span>
                  </div>
                  <p className="text-cyan-300/90 text-[10px] sm:text-xs tracking-[0.22em] uppercase font-bold ml-11">Member Access Card</p>
                </div>
                <div className="bg-white p-2 sm:p-2.5 rounded-2xl shadow-lg border border-white/40 shrink-0">
                  <QRCodeSVG />
                </div>
              </div>

              {/* Member Info */}
              <div className="flex items-center justify-between gap-4 mb-6 sm:mb-7">
                <div className="min-w-0">
                  <p className="text-cyan-200/70 text-[10px] sm:text-xs uppercase tracking-[0.22em] mb-1 font-bold">Member Name</p>
                  <p className="text-white text-xl sm:text-2xl font-black tracking-wide drop-shadow-sm truncate">{memberName}</p>
                </div>
                <div className="w-10 h-7 rounded-md bg-gradient-to-tr from-amber-300 via-amber-200 to-yellow-100 border border-amber-400/90 shadow-xs flex flex-col justify-between p-1 shrink-0 opacity-95">
                  <div className="w-full h-px bg-amber-600/40" />
                  <div className="flex justify-between w-full">
                    <div className="w-2.5 h-1.5 border border-amber-600/40 rounded-xs" />
                    <div className="w-2.5 h-1.5 border border-amber-600/40 rounded-xs" />
                  </div>
                  <div className="w-full h-px bg-amber-600/40" />
                </div>
              </div>

              <div className="mb-6 sm:mb-7">
                <p className="text-cyan-200/70 text-[10px] sm:text-xs uppercase tracking-[0.22em] mb-1 font-bold">Member ID</p>
                <p className="text-cyan-300 font-mono text-lg sm:text-xl font-bold tracking-[0.18em] drop-shadow-sm">{activeMemberId}</p>
              </div>

              {/* Barcode */}
              <div className="rounded-xl overflow-hidden bg-black/30 border border-white/10 p-2 backdrop-blur-xs">
                <BarcodeStripes />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex flex-col sm:flex-row gap-4 sm:gap-5 mt-6 sm:mt-8 pt-2">
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex-1 rounded-xl h-12 px-6 bg-gradient-to-r from-primary via-blue-600 to-cyan-600 hover:from-primary/90 hover:to-cyan-600/90 text-white border-0 gap-2.5 font-bold shadow-md shadow-primary/25 transition-all text-sm sm:text-base"
            data-testid="button-download-card"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download Card
          </Button>
          <Button
            onClick={handleShare}
            variant="outline"
            className="flex-1 rounded-xl h-12 px-6 gap-2.5 font-bold border-slate-200 dark:border-slate-700 bg-white dark:bg-card hover:bg-slate-50 dark:hover:bg-slate-800 transition-all shadow-xs text-sm sm:text-base"
            data-testid="button-share-card"
          >
            <Share2 className="w-4 h-4" /> Share
          </Button>
        </motion.div>
      </div>
    </MemberLayout>
  );
}

